import { useRemoteData } from "@sinch/mfe-helper-react";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { z } from "zod";
import { getMessageMediaApiUrlMiddleware } from "../config";

const SUPPORT_ACTIVE = "support.active";
const PARTNERS_ACTIVE_CUSTOMER_ACCOUNT = "partners.customer-accounts.active";

dayjs.extend(utc);
dayjs.extend(timezone);

const baseAccountZod = z.object({
  accountId: z.string(),
  accountName: z.string().nullable(),
});

type AccountType = z.infer<typeof baseAccountZod> & {
  children?: AccountType[] | undefined;
};

export const accountZod: z.ZodType<AccountType> = baseAccountZod.extend({
  children: z.lazy(() => accountZod.array()).optional(),
});

export const accountListResponseZod = z.object({
  accounts: z.array(accountZod),
});

export type Account = z.infer<typeof accountZod>;
export type AccountListResponse = z.infer<typeof accountListResponseZod>;

// TODO: Move account list logic to BFF
export const useAccountList = (account: Account, rights?: string[]) => {
  const isRightsSet = !!rights;
  const isSupportUser =
    isRightsSet &&
    (rights.includes(SUPPORT_ACTIVE) ||
      rights.includes(PARTNERS_ACTIVE_CUSTOMER_ACCOUNT));
  const accountListRemote = useRemoteData<Account[]>(
    `${getMessageMediaApiUrlMiddleware()}/iam/v1/accounts/list`,
    {
      enabled: isRightsSet && !isSupportUser,
      useToken: true,
      parse: (response) => {
        const parsed = accountListResponseZod.safeParse(response);
        if (!parsed.success) {
          const errorMessage = "fetchAccountList_failedToParse:";
          console.error(errorMessage, parsed.error);
          throw new Error(errorMessage + parsed.error);
        }
        return parsed.data.accounts;
      },
    }
  );

  const accountTreeRemote = useRemoteData<Account[]>(
    `${getMessageMediaApiUrlMiddleware()}/iam/v1/accounts/${
      account.accountId
    }/tree`,
    {
      enabled: isRightsSet && isSupportUser,
      useToken: true,
      parse: (response) => {
        const parsed = accountListResponseZod.safeParse(response);
        if (!parsed.success) {
          const errorMessage = "fetchAccountList_failedToParse:";
          console.error(errorMessage, parsed.error);
          throw new Error(errorMessage + parsed.error);
        }
        return parsed.data.accounts;
      },
    }
  );

  const accountListState = isSupportUser
    ? accountTreeRemote.getState()
    : accountListRemote.getState();

  return accountListState.status === "success"
    ? accountListState.data
    : undefined;
};
